import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { nhiParamsApi, F937 } from '../../../services/nhiParamsApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useNHIParamsForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F937[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [salary, setSalary] = useState(0);
  const [empAmount, setEmpAmount] = useState(0);
  const [orgAmount, setOrgAmount] = useState(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await nhiParamsApi.list(1, 200); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setSalary(current.f937_01); setEmpAmount(current.f937_11); setOrgAmount(current.f937_21); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };
  const startAdd = () => { setMode('add'); setSalary(0); setEmpAmount(0); setOrgAmount(0); };
  const startEdit = () => { if (!current) return; setMode('edit'); setSalary(current.f937_01); setEmpAmount(current.f937_11); setOrgAmount(current.f937_21); };
  const cancel = () => { setMode('browse'); if (current) { setSalary(current.f937_01); setEmpAmount(current.f937_11); setOrgAmount(current.f937_21); } };

  const save = async () => {
    try {
      if (mode === 'add') { await nhiParamsApi.create({ f937_01: salary, f937_11: empAmount, f937_21: orgAmount }); message.success('新增成功'); }
      else { await nhiParamsApi.update(current!.f937_01, { f937_11: empAmount, f937_21: orgAmount }); message.success('修改成功'); }
      const data = await loadRecords();
      const t = mode === 'add' ? salary : current!.f937_01;
      const idx = data.findIndex(r => r.f937_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await nhiParamsApi.delete(current.f937_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const val = parseInt(t, 10);
    if (isNaN(val)) { message.warning('請輸入數字'); return; }
    const idx = records.findIndex(r => r.f937_01 >= val);
    if (idx >= 0) setCurrentIdx(idx);
    else message.warning('查無此投保薪資等級');
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    salary, setSalary, empAmount, setEmpAmount, orgAmount, setOrgAmount,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
